import { useCallback, useEffect, useState } from 'react'

import { F0Box, F0Dialog } from '@factorialco/f0-react'
import { Input, Textarea } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

type SaveDashboardDialogProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (title: string, description: string) => Promise<void>
  defaultTitle: string
  defaultDescription?: string
}

export function SaveDashboardDialog({
  isOpen,
  onClose,
  onSave,
  defaultTitle,
  defaultDescription = '',
}: SaveDashboardDialogProps) {
  const [title, setTitle] = useState(defaultTitle)
  const [description, setDescription] = useState(defaultDescription)
  const [isSaving, setIsSaving] = useState(false)

  // Reset local state when the dialog opens so reopening with new defaults
  // doesn't keep stale values from the previous open.
  useEffect(() => {
    if (isOpen) {
      setTitle(defaultTitle)
      setDescription(defaultDescription)
    }
  }, [isOpen, defaultTitle, defaultDescription])

  const handleSave = useCallback(async () => {
    if (!title.trim() || isSaving) return
    setIsSaving(true)
    try {
      await onSave(title.trim(), description.trim())
      onClose()
    } finally {
      setIsSaving(false)
    }
  }, [title, description, isSaving, onSave, onClose])

  const isTitleEmpty = !title.trim()

  return (
    <F0Dialog
      position="center"
      isOpen={isOpen}
      onClose={onClose}
      width="sm"
      title={i18n.t('common.ai.dashboard.save_dialog.title')}
      container={null}
      primaryAction={{
        label: i18n.t('common.ai.dashboard.save_dialog.save'),
        onClick: handleSave,
        loading: isSaving,
        disabled: isSaving || isTitleEmpty,
      }}
      secondaryAction={{
        label: i18n.t('common.ai.dashboard.save_dialog.cancel'),
        onClick: onClose,
        disabled: isSaving,
      }}
    >
      <F0Box display="flex" flexDirection="column" gap="xl">
        <Input
          autoFocus
          label={i18n.t('common.ai.dashboard.save_dialog.title_label')}
          value={title}
          onChange={(value) => setTitle(value)}
          size="md"
          type="text"
        />
        <Textarea
          label={i18n.t('common.ai.dashboard.save_dialog.description_label')}
          placeholder={i18n.t('common.ai.dashboard.save_dialog.description_placeholder')}
          value={description}
          onChange={(value) => setDescription(value)}
          size="md"
          rows={10}
        />
      </F0Box>
    </F0Dialog>
  )
}
